import styled from 'styled-components';
import { breakpoints, sizes } from '../../theme';
import useKeyboardTrap from '../../hooks/useKeyboardTrap';
import { BodyRegular, Card, Flex, Headline2 } from '../Elements';
import Notification, { LinkToHelp, NotificationType } from '../notifiers/Notification';
import strings from '../../strings/strings.json';
import { ButtonColour } from 'components/buttons/ButtonStyle';
import Button from 'components/buttons/Button';

export type AlertError = {
    title: string;
    description: string;
    linkToHelp?: LinkToHelp;
};

type AlertProps = {
    alertText: string | string[];
    buttonText: string;
    ariaButtonText: string;
    buttonColour?: ButtonColour;
    onSubmit: (() => Promise<void>) | (() => void);
    closeAlert: Function;
    error?: AlertError;
    title?: string;
    submitting?: boolean;
};

export default function Alert({
    alertText,
    buttonText,
    ariaButtonText,
    buttonColour = 'PRIMARY',
    onSubmit,
    closeAlert,
    error,
    title,
    submitting = false,
}: AlertProps): JSX.Element {
    alertText = typeof alertText === 'string' ? [alertText] : alertText;

    return (
        <AlertBackground id="alert">
            <AlertBodyContainer>
                <AlertBodyComponent
                    closeAlert={closeAlert}
                    title={title}
                    alertText={alertText}
                    submitButtonTitle={buttonText}
                    submitButtonAria={ariaButtonText}
                    submitButtonColour={buttonColour}
                    submitButtonOnClick={() => onSubmit()}
                    error={error}
                    submitting={submitting}
                />
            </AlertBodyContainer>
        </AlertBackground>
    );
}

const AlertBodyContainer = styled.div`
    position: absolute;
    top: 10%;

    @media (max-width: ${breakpoints.sm}) {
        top: 20%;
        margin: 0 ${sizes.spacingMd};
    }
`;

type AlertBodyComponentProps = {
    closeAlert: Function;
    title?: string;
    alertText: string | string[];
    submitButtonTitle: string;
    submitButtonAria: string;
    submitButtonColour?: ButtonColour;
    submitButtonOnClick: () => void;
    error?: AlertError;
    submitting?: boolean;
};

export function AlertBodyComponent({
    closeAlert,
    title,
    alertText,
    submitButtonTitle,
    submitButtonAria,
    submitButtonOnClick,
    submitButtonColour,
    error,
    submitting = false,
}: AlertBodyComponentProps): JSX.Element {
    const { ref } = useKeyboardTrap(() => closeAlert());

    alertText = typeof alertText === 'string' ? [alertText] : alertText;

    return (
        <AlertBody id="alertBody" ref={ref} role="dialog" aria-modal="true">
            <Headline2>{title ? title : strings.alert.areYouSureDefaultText}</Headline2>

            {alertText.length >= 1 && (
                <div>
                    {alertText.map((text, index) => (
                        <BodyRegular key={index}>{text}</BodyRegular>
                    ))}
                </div>
            )}

            <ButtonContainer>
                <Button
                    type="button"
                    title={strings.alert.backButton.title}
                    aria={strings.alert.backButton.aria}
                    onClick={() => closeAlert()}
                />
                <Button
                    type="button"
                    title={submitButtonTitle}
                    aria={submitButtonAria}
                    onClick={() => submitButtonOnClick()}
                    colour={submitButtonColour}
                    submitting={submitting}
                />
            </ButtonContainer>
            {error && (
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={error.title}
                    description={error.description}
                    linkToHelp={error.linkToHelp}
                    maxWidth={'25rem'}
                />
            )}
        </AlertBody>
    );
}

const AlertBackground = styled(Flex)`
    position: fixed;
    justify-content: center;
    top: 80px;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);

    z-index: 10;
    @media (max-width: ${breakpoints.sm}) {
        top: 50px;
    }
`;

const AlertBody = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
    align-items: center;
    text-align: center;
    min-height: 300px;
    min-width: 600px;
    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingMd};
    }
`;

const ButtonContainer = styled(Flex)`
    gap: 2rem;
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column-reverse;
        width: 100%;
    }
`;
